import { KeyboardEvent, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Label, CartesianGrid } from 'recharts'
import { SymptomChartType } from '@estercare/ester-shared'
import styled from 'styled-components'
import { Title5 } from '../styles/Typography'
import { ChartLegend, LegendVariant } from './Legend'
import { ChartContainer, axisStyle, chartXAxisPadding } from './shared'
import { Caret } from '../icons/Caret'
import { theme } from '../../theme/theme'
import { MultiLineChartData, MultiLineChartLine } from './type'

type Props = {
  isMobileDevice: boolean
  data: MultiLineChartData[]
  lines: MultiLineChartLine[]
  title: string
  label: string
  scale?: number[]
  legendVariant?: LegendVariant
  collapsible?: boolean
  defaultOpen?: boolean
  chartType?: SymptomChartType
}

export const MultiLineChart = ({
  isMobileDevice,
  data,
  lines,
  title,
  label,
  scale,
  legendVariant = 'default',
  collapsible = false,
  defaultOpen = true,
  chartType = 'linear',
}: Props) => {
  const [open, setOpen] = useState(defaultOpen)
  const isOpen = !collapsible || open
  const Chart = chartType === 'bar' ? BarChart : LineChart

  const toggle = () => setOpen((previous) => !previous)
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      toggle()
    }
  }

  return (
    <ChartContainer>
      <Header $open={isOpen}>
        <TitleRow
          $clickable={collapsible}
          onClick={collapsible ? toggle : undefined}
          onKeyDown={collapsible ? handleKeyDown : undefined}
          role={collapsible ? 'button' : undefined}
          tabIndex={collapsible ? 0 : undefined}
          aria-expanded={collapsible ? open : undefined}
        >
          <Title5>{title}</Title5>
          {collapsible && (
            <CaretWrapper $open={open}>
              <Caret />
            </CaretWrapper>
          )}
        </TitleRow>
      </Header>
      <AnimatePresence initial={false}>
        {isOpen && (
          <AnimatedContent
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <ChartLegend
              type="dots"
              variant={legendVariant}
              align="start"
              data={lines.map(({ dataKey, color, name }) => ({ name: name ?? dataKey, color }))}
            />
            <ResponsiveContainer width="100%" height={300}>
              <Chart
                data={data}
                margin={{
                  right: isMobileDevice ? 5 : 30,
                  left: isMobileDevice ? -15 : 0,
                }}
              >
                <CartesianGrid horizontal vertical={false} stroke={theme.color.grey2} />
                <XAxis
                  dataKey={isMobileDevice ? 'nameMobile' : 'name'}
                  padding={chartXAxisPadding(isMobileDevice)}
                  axisLine={false}
                  tickLine={false}
                  tick={axisStyle}
                  interval={0}
                  height={50}
                />
                <YAxis
                  domain={scale ? [0, Math.max(...scale)] : [0, 'dataMax']}
                  ticks={scale ?? [0, 10, 20, 30, 40]}
                  axisLine={false}
                  tickLine={false}
                  tick={axisStyle}
                  width={isMobileDevice ? 40 : undefined}
                >
                  {!isMobileDevice && (
                    <Label
                      fontWeight={500}
                      fontSize={theme.fontSize.xxsmall}
                      fontFamily={theme.font.body}
                      dx={-20}
                      value={label}
                      position="center"
                      angle={-90}
                    />
                  )}
                </YAxis>
                {chartType === 'bar'
                  ? lines.map(({ dataKey, color }) => (
                      <Bar key={dataKey} dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
                    ))
                  : lines.map(({ dataKey, color }) => (
                      <Line
                        key={dataKey}
                        type="monotone"
                        dataKey={dataKey}
                        stroke={color}
                        strokeWidth={2}
                        dot={{ r: 4, fill: theme.color.white, stroke: color, strokeWidth: 2 }}
                      />
                    ))}
              </Chart>
            </ResponsiveContainer>
          </AnimatedContent>
        )}
      </AnimatePresence>
    </ChartContainer>
  )
}

const Header = styled.div<{ $open: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${theme.spacing.small}px;
  padding: 0 ${theme.spacing.tiny}px;
  margin-bottom: ${({ $open }) => ($open ? theme.spacing.small : 0)}px;
`

const TitleRow = styled.div<{ $clickable: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`

const CaretWrapper = styled.span<{ $open: boolean }>`
  display: inline-flex;
  transition: transform 0.3s ease;
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
`

const AnimatedContent = styled(motion.div)`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.small}px;
`
